import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Armchair,
  BarChart3,
  ChefHat,
  ChevronRight,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  PlugZap,
  ReceiptText,
  Settings,
  UserRound,
  UtensilsCrossed,
  Wallet,
  X,
} from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import type { Me } from "../lib/api";
import { initials, roleLabel } from "../lib/format";

const navigation = [
  {
    label: "Workspace",
    items: [
      {
        to: "/app",
        label: "Dashboard",
        icon: LayoutDashboard,
        end: true,
        roles: ["owner", "admin", "supervisor"],
      },
      {
        to: "/app/orders",
        label: "Pesanan",
        icon: ReceiptText,
        roles: ["owner", "admin", "supervisor", "cashier", "waiter", "kitchen"],
      },
      {
        to: "/app/menu",
        label: "Menu",
        icon: UtensilsCrossed,
        roles: ["owner", "admin"],
      },
      {
        to: "/app/tables",
        label: "Meja & QR",
        icon: Armchair,
        roles: ["owner", "admin", "supervisor", "waiter"],
      },
    ],
  },
  {
    label: "Operasional",
    items: [
      {
        to: "/app/kitchen",
        label: "Dapur",
        icon: ChefHat,
        roles: ["owner", "admin", "supervisor", "kitchen"],
      },
      {
        to: "/app/reports",
        label: "Laporan",
        icon: BarChart3,
        roles: ["owner", "admin"],
      },
    ],
  },
  {
    label: "Pengaturan",
    items: [
      {
        to: "/app/billing",
        label: "Billing & deposit",
        icon: Wallet,
        roles: ["owner"],
      },
      {
        to: "/app/integrations",
        label: "Integrasi",
        icon: PlugZap,
        roles: ["owner", "admin"],
      },
      {
        to: "/app/settings",
        label: "Pengaturan",
        icon: Settings,
        roles: ["owner"],
      },
    ],
  },
  {
    label: "Akun",
    items: [
      {
        to: "/app/profile",
        label: "Profil",
        icon: UserRound,
        roles: ["owner", "admin", "supervisor", "cashier", "waiter", "kitchen"],
      },
    ],
  },
];

const pageTitles: Record<string, string> = {
  "/app": "Dashboard",
  "/app/orders": "Pesanan",
  "/app/menu": "Menu",
  "/app/tables": "Meja & QR",
  "/app/kitchen": "Dapur",
  "/app/reports": "Laporan",
  "/app/billing": "Billing & deposit",
  "/app/integrations": "Integrasi",
  "/app/settings": "Pengaturan",
  "/app/profile": "Profil",
};

export function AppShell({
  me,
  onLogout,
}: {
  me: Me;
  onLogout: () => Promise<void>;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const title = pageTitles[location.pathname] ?? "Workspace";
  const today = new Intl.DateTimeFormat("id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());
  const visibleNavigation = navigation
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.roles.includes(me.membership.role),
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="app-shell">
      {mobileOpen ? (
        <button
          type="button"
          className="sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Tutup navigasi"
        />
      ) : null}

      <aside className={`sidebar${mobileOpen ? " is-open" : ""}`}>
        <div className="sidebar__brand">
          <div className="brand-lockup brand-lockup--sidebar">
            <BrandMark />
            <div>
              <strong>Cafe System Control</strong>
              <span>Workspace</span>
            </div>
          </div>
          <button
            type="button"
            className="sidebar__close"
            onClick={() => setMobileOpen(false)}
            aria-label="Tutup navigasi"
          >
            <X size={18} />
          </button>
        </div>

        <div className="sidebar__tenant">
          <div className="sidebar__tenant-avatar">
            {initials(me.tenant.name)}
          </div>
          <div>
            <strong>{me.tenant.name}</strong>
            <span>
              {me.membership.role === "owner"
                ? "Owner workspace"
                : "Staff workspace"}
            </span>
          </div>
        </div>

        <nav className="sidebar__nav">
          {visibleNavigation.map((group) => (
            <div className="nav-group" key={group.label}>
              <p className="nav-group__label">{group.label}</p>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `nav-link${isActive ? " is-active" : ""}`
                    }
                    onClick={() => setMobileOpen(false)}
                  >
                    <Icon size={17} />
                    <span>{item.label}</span>
                    <ChevronRight className="nav-link__chevron" size={14} />
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="user-chip">
            <span className="avatar">{initials(me.user.name)}</span>
            <div>
              <strong>{me.user.name}</strong>
              <span>{roleLabel(me.membership.role)}</span>
            </div>
          </div>
          <button
            type="button"
            className="icon-button icon-button--dark"
            onClick={() => void onLogout()}
            aria-label="Keluar"
            title="Keluar"
          >
            <LogOut size={17} />
          </button>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <div className="topbar__left">
            <button
              type="button"
              className="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              aria-label="Buka navigasi"
            >
              <Menu size={20} />
            </button>
            <Link
              to="/app"
              className="topbar-home"
              aria-label="Kembali ke dashboard"
              title="Kembali ke dashboard"
            >
              <Home size={16} />
              <span>Dashboard</span>
            </Link>
            <Link
              to="/app"
              className="topbar__brand"
              aria-label="Cafe System Control — dashboard"
              title="Kembali ke dashboard"
            >
              <BrandMark size="sm" />
            </Link>
            <div className="breadcrumb">
              <Link to="/app">Workspace</Link>
              <ChevronRight size={13} />
              <strong>{title}</strong>
            </div>
          </div>

          <div className="topbar__right">
            <span className="system-pill">
              <span className="system-pill__dot" />
              Sistem online
            </span>
            <span className="topbar__date">{today}</span>
            <Link
              to="/app/profile"
              className="topbar__account"
              aria-label={`Buka profil ${me.user.name}`}
              title={`${me.user.name} · ${roleLabel(me.membership.role)} · ${me.tenant.name}`}
            >
              <span className="avatar avatar--small">
                {initials(me.user.name)}
              </span>
              <span className="topbar__account-copy">
                <strong>{me.user.name}</strong>
                <small>
                  @{me.user.username} · {roleLabel(me.membership.role)}
                </small>
              </span>
            </Link>
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
